import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatPrice, formatKm, formatApiError } from "@/lib/api";
import { ArrowLeft, Calendar, Gauge, Fuel, Cog, Zap, Palette, Car, CheckCircle2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => {
  if (!u) return FALLBACK_IMG;
  return u.startsWith("/api/") ? `${BACKEND}${u}` : u;
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`)
      .then((r) => { setVehicle(r.data); setForm((f) => ({ ...f, message: `Ich interessiere mich für: ${r.data.make} ${r.data.model} (Baujahr ${r.data.year}). Bitte um Kontakt.` })); })
      .catch(() => navigate("/fahrzeuge"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, vehicle_id: id });
      toast.success("Anfrage gesendet — wir melden uns zeitnah.");
      setForm({ name: "", email: "", phone: "", message: form.message });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto p-12" data-testid="detail-loading">Lade…</div>;
  if (!vehicle) return null;

  const images = (vehicle.images?.length ? vehicle.images : [FALLBACK_IMG]).map(resolveImg);

  const specs = [
    { icon: Calendar, label: "Erstzulassung", value: vehicle.first_registration },
    { icon: Gauge, label: "Kilometerstand", value: formatKm(vehicle.mileage) },
    { icon: Fuel, label: "Kraftstoff", value: vehicle.fuel_type },
    { icon: Cog, label: "Getriebe", value: vehicle.transmission },
    { icon: Zap, label: "Leistung", value: `${vehicle.power_ps} PS` },
    { icon: Palette, label: "Farbe", value: vehicle.color },
    { icon: Car, label: "Karosserie", value: vehicle.body_type },
    { icon: Calendar, label: "Baujahr", value: vehicle.year },
  ];

  return (
    <div data-testid="detail-page" className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12">
      <Link to="/fahrzeuge" data-testid="detail-back" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-8">
        <ArrowLeft size={14} /> Zurück zur Übersicht
      </Link>

      {/* Header */}
      <div className="mb-8 animate-fade-up">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
          <span className="divider-thin" />{vehicle.make}
        </div>
        <h1 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.05]">{vehicle.model}</h1>
        <div className="mt-3 text-slate-500 text-sm flex items-center gap-3 flex-wrap">
          <span>{vehicle.first_registration}</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full" />
          <span>{formatKm(vehicle.mileage)}</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full" />
          <span>{vehicle.fuel_type}</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full" />
          <span>{vehicle.power_ps} PS</span>
        </div>
      </div>

      {/* Gallery */}
      <div className="grid md:grid-cols-4 gap-3 mb-12">
        <div className="md:col-span-3 aspect-[16/10] overflow-hidden bg-slate-100 border border-slate-200">
          <img src={images[activeImg]} alt={vehicle.model} className="w-full h-full object-cover" />
        </div>
        <div className="md:max-h-[60vh] md:overflow-y-auto pr-1">
          <div className="grid grid-cols-4 md:grid-cols-2 gap-3">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`aspect-[4/3] overflow-hidden border-2 ${activeImg === i ? "border-slate-900" : "border-slate-200 hover:border-slate-400"} transition-crisp`}
              >
                <img src={img} alt={`Bild ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          {images.length > 1 && (
            <p className="text-[10px] text-slate-500 mt-2 text-center md:text-left">
              {activeImg + 1} / {images.length}
            </p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-12">
        {/* Left content */}
        <div className="md:col-span-2 space-y-12">
          {/* Specs */}
          <section>
            <h2 className="font-heading font-bold text-2xl tracking-tight mb-6 text-slate-900">Technische Daten</h2>
            <div className="grid grid-cols-2 gap-y-5 gap-x-8 border-t border-slate-200 pt-6">
              {specs.map((s) => (
                <div key={s.label} className="flex items-start gap-3">
                  <s.icon size={18} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.label}</div>
                    <div className="font-medium text-slate-900">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Description */}
          <section>
            <h2 className="font-heading font-bold text-2xl tracking-tight mb-4 text-slate-900">Beschreibung</h2>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{vehicle.description}</p>
          </section>

          {/* Equipment */}
          {vehicle.equipment?.length > 0 && (
            <section>
              <h2 className="font-heading font-bold text-2xl tracking-tight mb-4 text-slate-900">Ausstattung</h2>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 border-t border-slate-200 pt-6">
                {vehicle.equipment.map((eq, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 size={16} className="text-slate-900 mt-0.5 flex-shrink-0" />
                    <span>{eq}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sticky sidebar */}
        <aside className="md:col-span-1">
          <div className="sticky top-28 space-y-4">
            <div className="border border-slate-900 p-6 bg-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Preis (inkl. MwSt.)</div>
              <div className="font-heading font-bold text-4xl text-slate-900 mt-1 leading-none" data-testid="detail-price">{formatPrice(vehicle.price)}</div>
              <div className="text-[10px] text-slate-500 mt-2" data-testid="detail-tax-type">
                {vehicle.tax_type === "regel"
                  ? "Regelbesteuerung · MwSt. ausweisbar (Vorsteuerabzug möglich)"
                  : "Differenzbesteuert nach § 24 UStG · USt. nicht ausweisbar"}
              </div>
              {vehicle.sold && <div className="mt-3 inline-block bg-red-600 text-white px-3 py-1 text-xs uppercase tracking-[0.2em]">Verkauft</div>}

              <div className="mt-6 pt-6 border-t border-slate-200 space-y-3 text-sm">
                <a href="tel:+436677608726" data-testid="detail-call" className="flex items-center justify-between hover:text-red-600 transition-crisp group">
                  <span className="flex items-center gap-2"><Phone size={14} /> Anrufen</span>
                  <span className="font-medium group-hover:translate-x-1 transition-transform">+43 667 760 8726</span>
                </a>
                <a href="mailto:kontakt@dr-automobile.at" data-testid="detail-email" className="flex items-center gap-2 hover:text-red-600 transition-crisp">
                  <Mail size={14} /> kontakt@dr-automobile.at
                </a>
              </div>
            </div>

            <form onSubmit={submit} className="border border-slate-200 p-6 space-y-3" data-testid="detail-contact-form">
              <h3 className="font-heading font-semibold text-base text-slate-900">Anfrage zum Fahrzeug</h3>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" data-testid="form-name" className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail *" data-testid="form-email" className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefon" data-testid="form-phone" className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Nachricht *" data-testid="form-message" className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900 resize-none" />
              <button type="submit" disabled={sending} data-testid="form-submit" className="w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-red-600 transition-crisp disabled:opacity-50">
                {sending ? "Sende…" : "Anfrage senden"}
              </button>
              <p className="text-[10px] text-slate-500">Mit Absenden stimmen Sie zu, dass wir Sie zur Bearbeitung Ihrer Anfrage kontaktieren.</p>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
